import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import auth from "../utils/auth";
import "../styles/OrderHistory.css";
import { scrollToTop } from "../hooks/useScrollToTop";

const OrderHistory = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(3);

  const fetchOrders = useCallback(async () => {
    if (!auth.isAuthenticated()) {
      auth.logout();
      navigate("/logga-in");
      scrollToTop();
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/user/orders`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch orders: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const ordersList = Array.isArray(data.orders) ? data.orders : [];

      setOrders(ordersList);
    } catch (error) {
      setError(error.message || "Det gick inte att hämta dina beställningar. Försök igen senare.");
      console.error("Error fetching order history:", error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Behandlas";
      case "shipped":
        return "Skickad";
      case "returned":
        return "Returnerad";
      default:
        return "";
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "pending":
        return "orderhistory-status-pending";
      case "shipped":
        return "orderhistory-status-shipped";
      case "returned":
        return "orderhistory-status-returned";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("sv-SE", options);
  };

  const calculateSubtotal = (order) => {
    if (!order || !order.orderItems || !Array.isArray(order.orderItems) || order.orderItems.length === 0) {
      return 0;
    }
    return order.orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTotal = (order) => {
    if (!order) {
      return 0;
    }

    const subtotal = calculateSubtotal(order);

    let shippingCost = 65;

    if (order.shippingCost !== undefined) {
      shippingCost = order.shippingCost;
    }

    else if (order.orderItems.length > 0 && order.orderItems[0].shippingCost !== undefined) {
      shippingCost = order.orderItems[0].shippingCost || 0;
    }

    return subtotal + shippingCost;
  };

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  const paginate = (pageNumber) => {
    const validPage = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage(validPage);
    scrollToTop();
  };

  const jumpBackward = () => {
    const newPage = Math.max(1, currentPage - 10);
    setCurrentPage(newPage);
    scrollToTop();
  };

  const jumpForward = () => {
    const newPage = Math.min(totalPages, currentPage + 10);
    setCurrentPage(newPage);
    scrollToTop();
  };

  if (isLoading) {
    return <div className="orderhistory-loading">Laddar beställningar...</div>;
  }

  return (
    <div className="orderhistory-container">
      <h2 className="orderhistory-title">Orderhistorik</h2>
      {error && <p className="orderhistory-error">{error}</p>}
      {orders.length === 0 ? (
        <p className="orderhistory-no-orders">Du har inga beställningar än.</p>
      ) : (
        <>
          <div className="orderhistory-list">
            {currentOrders.map((order) => (
              <div
                key={order._id}
                className="orderhistory-item"
              >
                <div className="orderhistory-header">
                  <div className="orderhistory-id">
                    <span className="orderhistory-label">Order</span>#{order.orderId}
                  </div>
                  <div className={`orderhistory-status ${getStatusClass(order.status)}`}>
                    {getStatusText(order.status)}
                  </div>
                </div>
                <div className="orderhistory-date">
                  <span className="orderhistory-label">Datum:</span> {formatDate(order.createdAt)}
                </div>
                <div className="orderhistory-items-summary">
                  <span className="orderhistory-label">Artiklar:</span> {order.orderItems.length} st
                </div>
                <div className="orderhistory-product-ids">
                  <span className="orderhistory-label">Produkt ID:</span>
                  {order.orderItems.map((item, index) => (
                    <span key={index} className="orderhistory-product-id">
                      {item.productId}{index < order.orderItems.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
                <div className="orderhistory-total">
                  <span className="orderhistory-label">Totalt:</span> {calculateTotal(order)} kr
                </div>
                <div className="orderhistory-address">
                  <span className="orderhistory-label">Leveransdetaljer:</span>
                  <div className="orderhistory-address-details">
                    {order.shippingAddress.careOf && <div>C/O {order.shippingAddress.careOf}</div>}
                    <div>{order.shippingAddress.street}</div>
                    <div>{order.shippingAddress.postalCode} {order.shippingAddress.region}</div>
                    <div>{order.shippingAddress.country}</div>
                  </div>
                </div>
                <button
                  className="orderhistory-view-button"
                  onClick={() => handleOrderClick(order.orderId)}
                >
                  Visa detaljer
                </button>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="orderhistory-pagination">
              <button
                onClick={jumpBackward}
                disabled={currentPage <= 1}
                className="orderhistory-pagination-button"
              >
                &larr; 10
              </button>
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="orderhistory-pagination-button"
              >
                Föregående
              </button>
              <div className="orderhistory-page-indicator">
                {currentPage} / {totalPages}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="orderhistory-pagination-button"
              >
                Nästa
              </button>
              <button
                onClick={jumpForward}
                disabled={currentPage >= totalPages}
                className="orderhistory-pagination-button"
              >
                10 &rarr;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrderHistory;